/* eslint-disable max-lines */
// HACK 500行を超過(1000行オーバー)しているため、更にコンパクトにする必要がある
/************************************************/
/*          About dayjs                         */
/************************************************/
// The MIT License (MIT)
//
// Copyright (c) 2018-present, iamkun
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import dayjs from 'dayjs';

import { THIN, NORMAL, THICK, DASHED, DOTTED, LINE_WIDTH, STYLE, UPDATE_TYPE, COLOR, LEGEND_DEFS, SOLID } from './Graph';
import c from '../config';

const ONE_DAY_MS = 86400000;
const ONE_HOUR = 3600;
const ONE_SECOND = 1;
const TEN_SECONDS_MS = 10000;
const ONE_SECOND_MS = 1000;
const TEN_MS = 10;

// 経過時間の初期値(秒)
const DEFAULT_PASSED_TIME_SEC = "00:00";
// 経過時間の初期値(ミリ秒)
const DEFAULT_PASSED_TIME_MSEC = `${DEFAULT_PASSED_TIME_SEC}.000`;

const COLORCODE = /^#([0-9a-f]{3}){1,2}$/i;
const UTC = dayjs.unix(0);

export const DRAW_METHOD_ALL = 0;
export const DRAW_METHOD_RECENT = 1;
export const DEFAULT_ZOOM_PAN_MODE = 'xy';
export const ZOOM_PAN_MODE_X = 'x';

export class GraphMediator {
  // コンストラクタ
  // 本クラスの終了後はclose()を呼ぶこと
  constructor(drawMethod = DRAW_METHOD_RECENT) {
    this.graphElementList = [];
    this.linkedMediators = [];
    this.zoomPanStateChangedCallback = () => { };
    this.timelineElement = '';
    this.timerId = 0;
    this.markerHashArray = [];
    this.markerElement = '';
    this.buttonObjectHash = {};
    this.stepSize = ONE_SECOND;
    this.baseTime = null;
    this.drawMethod = drawMethod;
    this.zoomPanMode = DEFAULT_ZOOM_PAN_MODE;
  }

  /**
   * 本クラスの終了処理
   */
  close() {
    this.removeEventListener();
    this.timerId = 0;
    this.graphElementList.forEach((graphElement) => {
      graphElement.close();
    });
    this.timelineElement.close();
  }

  /**
   * データ投入中であることを示すためのフラグを立てる処理(CITS)
   *
   * @param {boolean} appending true: 投入中 false: 非投入中
   */
  setDataAppendingStatus(appending) {
    // データ投入中フラグ更新
    this.timelineElement.dataAppending = appending;

    if (!this.graphElementList || this.graphElementList.length === 0) {
      this.timelineElement.updateMarker(null);
      return;
    }

    // graph-elementの要素文ループ
    this.graphElementList.forEach((graphElement) => {
      // データ投入中フラグ更新
      graphElement.dataAppending = appending;

      // データ投入が完了した場合
      if (!appending && this.baseTime) {
        // グラフ表示開始時間とグラフ表示終了時間をタイムラインに同期させる
        graphElement.startTime = UTC.add(this.timelineElement._startTimeMs, 'ms');
        graphElement.endTime = UTC.add(this.timelineElement._endTimeMs, 'ms');
        graphElement.updateComponent(UPDATE_TYPE.X_MIN_MAX, {
          "xMin": graphElement.startTime,
          "xMax": graphElement.endTime
        });
        graphElement.updateComponent(UPDATE_TYPE.X_TICKS, null);

        // グラフ描画更新(時間初期化 => グラフ更新 => 再生バー更新の流れ)
        graphElement.initTime();
        graphElement.currentTime = UTC.add(this.timelineElement.currentTimeMs, 'ms');
        graphElement.drawCurrentLine();
      }

      // タイムラインのマーカをアップデート
      if (graphElement.markerLine.length > 0) {
        this.timelineElement.updateMarker(graphElement.markerLine.length);
      } else {
        // マーカがない場合は、タイムラインの現在地アイコンのみ更新
        this.timelineElement.syncCurrentPositionIcon();
      }
    });
  }

  /**
   * コンポーネントを更新する(CITS)
   */
  updateComponent() {
    // タイムライン上のマーカの位置を再計算してアップデートする
    if (this.timelineElement) {
      this.timelineElement.updateMarker();
    }

    // グラフ上のカレントラインとマーカラインの位置を再計算してアップデートする
    if (!this.graphElementList || this.graphElementList.length === 0) {
      return;
    }
    this.graphElementList.forEach((graphElement) => {
      // グラフ上のマーカ線をアップデート
      if (graphElement.markerLine.length > 0) {
        graphElement.updateMarker();
      }

      // グラフ描画更新(時間初期化 => グラフ更新 => 再生バー更新の流れ)
      graphElement.initTime();
      graphElement.currentTime = UTC.add(this.timelineElement.currentTimeMs, 'ms');
      graphElement.drawCurrentLine();
    });
  }

  addEventListener(buttonObjectHash = {}) {
    this.buttonObjectHash = buttonObjectHash;
    Object.entries(buttonObjectHash).forEach(([key, value]) => {
      switch (key) {
        case "play":
          try {
            value.addEventListener("click", this);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("play button element does not exist");
          }
          break;

        case "stop":
          try {
            value.addEventListener("click", this);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("stop button element does not exist");
          }
          break;

        case "pause":
          try {
            value.addEventListener("click", this);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("pause button element does not exist");
          }
          break;

        case "stepBackMax":
          try {
            value.addEventListener("click", this);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("stepBackMax button element does not exist");
          }
          break;

        case "stepBackMin":
          try {
            value.addEventListener("click", this);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("stepBackMin button element does not exist");
          }
          break;

        case "stepFwdMin":
          try {
            value.addEventListener("click", this);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("stepFwdMin button element does not exist");
          }
          break;

        case "stepFwdMax":
          try {
            value.addEventListener("click", this);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("stepFwdMax button element does not exist");
          }
          break;
        default:
          break;
      }
    });

    // スライダーのイベント登録
    const slider = this.timelineElement.getComponent();
    // スライダーがない場合、再生時刻時点変更・スライダのイベントを登録しないでスキップ
    if (slider === null) {
      return;
    }
    slider.noUiSlider.on('change', (values, handle) => {
      // 範囲変更ハンドルの操作時に、表示範囲を更新する
      const isChangeRange = handle === this.timelineElement.getStartKnobNumber() || handle === this.timelineElement.getEndKnobNumber();
      // 現在時刻ハンドルの操作時に、タイムラインシークする
      const isTimelineSeek = handle === this.timelineElement.getCurrentKnobNumber();

      if (isChangeRange) {
        this.changeRange(values);
      } else if (isTimelineSeek) {
        this.timelineSeek(values);
      }
    });
  }

  // ボタンのイベントを全削除
  removeEventListener() {
    Object.entries(this.buttonObjectHash).forEach(([, value]) => {
      value.removeEventListener("click", this);
    });
  }

  /**
   * 計測開始時間を設定する
   *
   * @param {Number} baseTime 計測開始時間
   * @param {Number} baseTimeOffset 計測開始時間のオフセット
   */
  setBaseTime(baseTime, baseTimeOffset) {
    this.baseTime = baseTime;
    this.graphElementList.forEach((graphElement) => {
      // グラフの基準時間を更新する
      graphElement.baseTime = baseTime;
      graphElement.baseTimeOffset = baseTimeOffset;
    });

    // タイムラインの基準時間を更新する
    this.timelineElement.setBaseTime(baseTime);
  }

  /**
   * ズームやパンのモードを設定する
   * @param {string} mode 指定するモード。'x': x軸のみ、'y': y軸のみ、'xy': x軸とy軸両方
   */
  setZoomPanMode(mode = DEFAULT_ZOOM_PAN_MODE) {
    this.zoomPanMode = mode;
    this.graphElementList.forEach((graphElement) => {
      graphElement.setZoomPanMode(this.zoomPanMode);
    });
  }

  setGraphElementList(ids, draggingCallback = () => { }) {
    if (!ids) {
      throw new Error('ids does not exist');
    }
    if (typeof ids !== 'object') {
      throw new Error('ids must be of type object');
    }
    const graphElementList = document.querySelectorAll('graph-element');
    const self = this;
    self.graphElementList = [];
    graphElementList.forEach((graphElement) => {
      ids.forEach((id) => {
        if (graphElement.id === id) {
          graphElement.deleteComponent();
          graphElement.setZoomPanMode(this.zoomPanMode);
          graphElement.setComponent();
          graphElement.initTime();

          // 各グラフのドラッグ処理のコールバックを追加
          const callbackFunc = (draggedElementId, cursorType, usingColors, time) => {
            if (cursorType && time) {
              draggingCallback(cursorType, time);
              // カーソルドラッグ時に、タイムラインも更新する
              this.timelineElement.deleteCursor(cursorType);
              this.timelineElement.setCursor(cursorType, time);
              // 分割グラフの場合に他のグラフにもカーソルのドラッグを反映
              this.graphElementList.forEach(graphElement => {
                if (graphElement._id !== draggedElementId) {
                  graphElement.graphSetCursor(cursorType, time);
                  graphElement.drawCurrentLine();
                }
              });
            }
          };
          graphElement.updateDraggingCallback(callbackFunc);

          // 各グラフのカレント変更時のコールバックを設定する
          graphElement.updateCurrentTimeSelectedCallback((_, time) => {
            this.seekToTime(time, true);
          });

          // ズーム・パン時に、他のグラフへX軸範囲を同期するコールバックを設定する
          graphElement.updateZoomPanSyncCallback((sourceGraphId, xMin, xMax) => {
            this.syncZoomPanRange(sourceGraphId, xMin, xMax);
          });

          // ズーム/パン状態変更時のコールバックを設定する
          graphElement.updateZoomPanStateChangedCallback((isPan) => {
            this.zoomPanStateChangedCallback(isPan);
          });

          // ダブルクリック時に全グラフのズームやパンをリセットするコールバックを設定する
          graphElement.updateResetZoomSyncCallback(() => {
            this.resetZoom(true);
          });

          self.graphElementList.push(graphElement);
        }
      });
    });
  }

  getGraphElementList() {
    return this.graphElementList;
  }

  /**
   * ズーム/パン同期先のGraphMediatorを設定する
   * @param {Array<GraphMediator>} mediators 連携先のGraphMediator配列
   */
  setLinkedMediators(mediators = []) {
    if (!Array.isArray(mediators)) {
      throw new Error('mediators must be of type object');
    }

    // !==thisで誤って自分自身を連携先に含めないようにする
    this.linkedMediators = mediators.filter((mediator) => mediator && mediator !== this);
  }

  /**
   * ズーム/パン状態変更時のコールバック関数を更新する
   * @param {Function} callback ズーム/パン状態変更時に実行するコールバック関数
   */
  updateZoomPanStateChangedCallback(callback) {
    this.zoomPanStateChangedCallback = callback;
  }

  /**
   * 全てのグラフのうち、ズーム/パン状態のものがあるか否か
   * @returns {boolean}
   */
  isAnyGraphZoomedOrPanned() {
    return this.graphElementList.some((graphElement) => graphElement?.isZoomedOrPanned?.());
  }

  setTimelineElement(id) {
    const timelineElements = document.querySelectorAll('time-line');
    const timelineElement = Object.values(timelineElements).find((elem) => (elem.id === id));
    this.timelineElement = timelineElement;
    this.timelineElement.setComponent();
    this.timelineElement.initTime();
  }

  getTimelineElement() {
    return this.timelineElement;
  }

  enableTimeline() {
    if (!this.timelineElement._timelineComponent) return null;
    const elementChild = this.timelineElement._timelineComponent.querySelectorAll('*');
    elementChild.forEach((element) => {
      if (element.localName.includes("marker")) {
        // マーカーをすべて表示する
        element.style.display = "inline";
      }
    });

    return this.timelineElement._timelineComponent.noUiSlider.enable();
  }

  disableTimeline() {
    if (!this.timelineElement._timelineComponent) return null;
    return this.timelineElement._timelineComponent.noUiSlider.disable();
  }

  // 描画線を削除する
  deleteGraphLine(id, itemId) {
    if (!id) {
      throw new Error('id does not exist');
    }
    if (typeof id !== 'string') {
      throw new Error('id must be of type string');
    }
    if (!itemId) {
      throw new Error('itemId does not exist');
    }
    if (typeof itemId !== 'string') {
      throw new Error('itemId must be of type string');
    }

    if (this.graphElementList === undefined) {
      return;
    }
    this.graphElementList.forEach((graphElement) => {
      if (graphElement.id === id) {
        const grComp = graphElement.getComponent();
        let delInd = null;
        let lastInd = 0;
        graphElement._yAxesInfo.forEach((inf, inde) => {
          if (inf.id === itemId) {
            delInd = inde;
            lastInd = inf.lastIndex;
            return;
          }
        });
        if (delInd !== null) {
          graphElement._yAxesInfo.splice(delInd, 1);
          grComp.data.datasets.splice(delInd, 1);

          //y軸ラベル変更用
          const elementId = `yLabelOuter${id}`;
          const elementParent = document.getElementById(elementId);
          const elementChild = elementParent.querySelectorAll('*');

          for (const element of elementChild) {
            const idPrefixMap = {
              "yLabelContainerMax": "max",
              "yLabelContainerMin": "min",
              "yLabelContainerItemName": "itemName"
            };
            const isRemoveTarget = Object.keys(idPrefixMap).find(idPrefix => element.id.includes(idPrefix));
            if (isRemoveTarget === undefined) {
              continue;
            }
            const itemIdPrefix = idPrefixMap[isRemoveTarget];

            const elementToRemoveItem = element.getElementsByTagName("p");
            if (elementToRemoveItem.length <= 0) {
              continue;
            }

            // ラベルのidが一致するindexを取得する
            const itemNameDelInd = Array.from(elementToRemoveItem).findIndex(item => item.id === `${itemIdPrefix}${id}${lastInd}`);
            element.removeChild(elementToRemoveItem[itemNameDelInd >= 0 ? itemNameDelInd : -1]);
          }
          graphElement._graphComponent.update();
        }
      }
    });
  }

  // イベントが発生する度に呼び出され、イベントオブジェクトを渡す
  // event.target.id ボタンのIDエレメント
  // event.type イベント種類 clickなど
  // objectにします
  handleEvent(event) {
    let target = null;

    switch (event.srcElement.tagName.toLowerCase()) {
      case 'div':
        target = this.getButtonObject(event.srcElement);
        break;
      case 'svg':
        target = this.getButtonObject(event.srcElement.closest('svg'));
        break;
      case 'path':
        target = this.getButtonObject(event.srcElement.closest('path'));
        break;
      default:
        target = event.target || event.srcElement;
    }

    switch (target) {
      case this.buttonObjectHash["play"]:
        this.play();
        break;

      case this.buttonObjectHash["stop"]:
        this.stop();
        break;

      case this.buttonObjectHash["pause"]:
        this.pause();
        break;

      case this.buttonObjectHash["clear"]:
        this.clear();
        break;

      case this.buttonObjectHash["stepBackMax"]:
        this.stepBackMax();
        break;

      case this.buttonObjectHash["stepBackMin"]:
        this.stepBackMin();
        break;

      case this.buttonObjectHash["stepFwdMin"]:
        this.stepFwdMin();
        break;

      case this.buttonObjectHash["stepFwdMax"]:
        this.stepFwdMax();
        break;
      default:
        break;
    }
  }

  /**
   * 指定されたオブジェクトの親のButtonタグを取得する
   * @param {*} child 対象のオブジェクト
   * @returns Buttonタグのオブジェクト
   */
  getButtonObject(child) {
    let i = 0;
    let parent = child;
    // 3階層上まで検索する
    while (i < 3) {
      parent = parent.parentNode;
      if (parent.tagName.toLowerCase() === 'button') {
        return parent;
      }

      i++;
    }
    return null;
  }

  // タイムラインにマーカを登録する
  setMarker(time, memo, uuid, tag, isUpdate = false) {
    if (time && typeof time !== 'number') {
      throw new Error('time must be of type number');
    }
    if (memo && typeof memo !== 'string') {
      throw new Error('memo must be of type string');
    }
    if (uuid && typeof uuid !== 'string') {
      throw new Error('uuid must be of type string');
    }
    this.timelineElement.setMarker(time, memo, uuid, tag);
    this.markerHashArray = this.timelineElement.getMarkerHashArray();
    this.markerElement = this.timelineElement.getMarkerElement();
    if (!isUpdate) {
      // 追加時のみマーカー線の追加を行う
      this.graphSetMarker(time);
    }
  }

  readMarker(obj) {
    if (!obj) {
      throw new Error('obj does not exist');
    }
    if (typeof obj !== 'object') {
      throw new Error('obj must be of type object');
    }
    let markerInfo = {};
    this.markerHashArray.forEach((markerHash) => {

      // 星の文字列をsvgに変更したことに伴う処理変更(CITS)
      let key = "";
      if (obj.srcElement.tagName.toLowerCase() === "svg") {
        key = obj.srcElement.closest('svg').parentNode.localName;
      } else if (obj.srcElement.tagName.toLowerCase() === "path") {
        key = obj.srcElement.closest('path').parentNode.parentNode.localName;
      } else {
        key = obj.target.localName;
      }

      if (markerHash[key] !== undefined) {
        markerInfo = markerHash[key];
        return;
      }
    });
    return markerInfo;
  }

  deleteMarker(obj, marker) {
    if (!obj) {
      throw new Error('obj does not exist');
    }
    if (typeof obj !== 'object') {
      throw new Error('obj must be of type object');
    }

    // 星の文字列をsvgに変更したことに伴う処理変更(CITS)
    const target = obj.target || obj.srcElement;
    const slider = this.timelineElement.getComponent();
    if (target.nodeName.toLowerCase() === "svg") {
      slider.removeChild(target.parentNode);
    } else if (target.nodeName.toLowerCase() === "path") {
      slider.removeChild(target.parentNode.parentNode);
    } else {
      slider.removeChild(target);
    }

    let markerInfo = {};
    this.markerHashArray = this.markerHashArray.filter((markerHash) => {
      const key = Object.keys(markerHash)[0];
      if (marker.localName === key) {
        markerInfo = markerHash[marker.localName];
        return false;
      }
      return true;
    });
    this.graphElementList.forEach((graphElement) => {
      graphElement.graphDeleteMarker(markerInfo);
      graphElement.drawCurrentLine();
    });

  }

  /**
   * UUID指定でマーカーを削除する
   * @param {string} uuid 削除するマーカーのUUID
   */
  deleteMarkerByUuid(uuid) {
    this.timelineElement.deleteMarker(uuid);

    let markerInfo = {};
    this.markerHashArray = this.markerHashArray.filter((markerHash) => {
      if (Object.values(markerHash)[0].markerUuid === uuid) {
        markerInfo = Object.values(markerHash)[0];
        return false;
      }
      return true;
    });

    this.graphElementList.forEach((graphElement) => {
      graphElement.graphDeleteMarker(markerInfo);
      graphElement.drawCurrentLine();
    });

  }

  stepSetting(stepSmall = ONE_SECOND_MS, stepLarge = TEN_SECONDS_MS) {
    if (typeof stepSmall !== 'number') {
      throw new Error('stepSmall must be of type number');
    }
    if (typeof stepLarge !== 'number') {
      throw new Error('stepLarge must be of type number');
    }
    if (stepLarge < stepSmall) {
      throw new Error('stepLarge is less than stepSmall');
    }
    this.timelineElement.stepSizeSmall = stepSmall;
    this.timelineElement.stepSizeLarge = stepLarge;
  }

  /**
   * x軸設定
   * @param {Number} stepSize X軸の間隔(sec)
   */
  xRangeSetting(stepSize) {
    if (!stepSize) {
      throw new Error('stepSize does not exist');
    }
    if (stepSize && typeof stepSize !== 'number') {
      throw new Error('stepSize must be of type number');
    }
    if (stepSize < ONE_SECOND || ONE_HOUR < stepSize) {
      throw new Error(`The stepSize needs to be set between ${ONE_SECOND} and ${ONE_HOUR}.`);
    }

    this.stepSize = stepSize;

    this.graphElementList.forEach((graphElement) => {
      graphElement.stepSize = this.stepSize;
      graphElement._graphData.options.scales.x.time.stepSize = this.stepSize;
      // データ投入が完了している場合
      if (!graphElement.dataAppending && this.baseTime) {
        // stepSizeにあわせてX軸を更新
        graphElement.updateComponent(UPDATE_TYPE.X_TICKS, null);
      }
    });
  }

  /**
   * y軸設定
   * @param {string} id 項目
   * @param {*} dataList データリスト
   */
  yRangeSetting(id, dataList) {
    if (!id) {
      throw new Error('id does not exist');
    }
    if (typeof id !== 'string') {
      throw new Error('id must be of type string');
    }
    if (!dataList) {
      throw new Error('dataList does not exist');
    }
    if (typeof dataList !== 'object') {
      throw new Error('dataList must be of type object');
    }

    this.graphElementList.forEach((graphElement) => {
      // graph-elementに設定されているidと変更したいグラフのidが一致するか確認
      if (graphElement.id === id) {
        // graphのデータを取得
        graphElement._graphData.data.datasets.forEach((dataset, index) => {
          Object.keys(dataList).forEach((key) => {
            const yMax = dataList[key].max;
            const yMin = dataList[key].min;
            if (dataset.label === key) {
              if (yMax && typeof yMax !== 'number') {
                throw new Error('max must be of type number');
              }
              if (yMin && typeof yMin !== 'number') {
                throw new Error('min must be of type number');
              }

              // Y軸のMax / Minを設定する
              graphElement._graphData.options.scales.y.ticks.max = yMax;
              graphElement._graphData.options.scales.y.max = yMax;
              graphElement._graphData.options.scales.y.ticks.min = yMin;
              graphElement._graphData.options.scales.y.min = yMin;

              graphElement._yAxesInfo[index].max = yMax;
              graphElement._yAxesInfo[index].min = yMin;

              // Y軸のメモリ幅を設定する
              graphElement._graphData.options.scales.y.ticks.stepSize = (yMax - yMin) / graphElement._graphData.options.scales.y.ticks.maxTicksLimit;

              const yLabelOuter = document.getElementById('yLabelOuter' + id);
              const yLabelContainerMax = yLabelOuter.querySelectorAll('#yLabelContainerMax' + id);
              const yLabelContainerMin = yLabelOuter.querySelectorAll('#yLabelContainerMin' + id);
              // ラベルに追加したindexを取得する
              const lastInd = graphElement._yAxesInfo[index].lastIndex;
              const maxId = `max` + id + lastInd;
              let targetIndex = 0;
              for (let num = 0; num < yLabelContainerMax[0].children.length; num++) {
                if (yLabelContainerMax[0].children[num].id === maxId) {
                  targetIndex = num;
                  break;
                }
              }

              // maxDispやminDispがundefinedのときはmin/maxの表示が消えてしまうので、textContentを更新しない
              // #16623でUDS側の呼び出し元からundefinedが混入する事象は別途対応する
              if (dataList[key].maxDisp !== undefined && dataList[key].minDisp !== undefined) {
                const unit = yLabelContainerMax[0].children[targetIndex].textContent.match(/\[[^\]]*\]/);
                if (unit) {
                  yLabelContainerMax[0].children[targetIndex].textContent = dataList[key].maxDisp + unit[0];
                  yLabelContainerMin[0].children[targetIndex].textContent = dataList[key].minDisp + unit[0];
                } else {
                  yLabelContainerMax[0].children[targetIndex].textContent = dataList[key].maxDisp;
                  yLabelContainerMin[0].children[targetIndex].textContent = dataList[key].minDisp;
                }
              }
            }

            // Y軸のMax / Minを設定する
            const grComp = graphElement.getComponent();
            grComp.data.datasets.map((x) => x.yAxisID).forEach((yAxisId) => {
              const yAxis = graphElement._graphData.options.scales[yAxisId];
              if (yAxis) {
                if ((dataset.label === key) && (dataset.yAxisID === yAxisId)) {
                  yAxis.max = yMax;
                  yAxis.min = yMin;
                }
              }
            });
          });
        });

        graphElement.drawCurrentLine();
      }
    });
  }

  // タイムラインシーク
  timelineSeek(values) {
    // 更新された現在時刻
    const newCurrentTimeMs = Number(values[(values.length === 1) ? 0 : 1]); // 1本のつまみの場合は0番目を参照(CITS)
    this.seekToTime(newCurrentTimeMs, true);
  }

  /**
   * 現在時刻を指定してシークする
   * @param {number} newCurrentTimeMs 新しい現在時刻(ms)
   * @param {boolean} propagateToLinked リンクされたmediatorに伝播するかどうか(他mediatorでの再帰呼び出し時はfalse)
   * @param {boolean} notifyHandleChange ハンドル変更を通知するかどうか
   */
  seekToTime(newCurrentTimeMs, propagateToLinked = false, notifyHandleChange = true) {
    if (!Number.isFinite(newCurrentTimeMs)) {
      return;
    }

    // タイムライン描画更新(時間を初期化 => 現在時刻変更 => 描画更新の流れ)
    this.timelineElement.initTime();
    this.timelineElement.currentTimeMs = newCurrentTimeMs;
    this.timelineElement.updateComponent();

    // グラフ描画更新(時間初期化 => グラフ更新 => 再生バー更新の流れ)
    this.graphElementList.forEach((graphElement) => {
      graphElement.initTime();
      graphElement.currentTime = UTC.add(this.timelineElement.currentTimeMs, 'ms');
      graphElement.drawCurrentLine();
    });

    if (propagateToLinked && Array.isArray(this.linkedMediators) && this.linkedMediators.length > 0) {
      this.linkedMediators.forEach((mediator) => {
        mediator.seekToTime(newCurrentTimeMs, false, false);
      });
    }

    if (notifyHandleChange && this.timelineElement._handleChange) {
      this.timelineElement._handleChange(this.timelineElement.currentTimeMs);
    }
  }

  // 現在再生時刻時点変更(範囲の場合の左側のつまみと右側のつまみを変更した時のイベント)
  changeRange(values) {
    if (this.timelineElement.getStartKnobNumber() === null || this.timelineElement.getEndKnobNumber() === null) {
      return;
    }
    // 1本のつまみの場合は開始時間／終了時間を変更しないようにする(CITS)
    if (values.length === 1) {
      // グラフをタイムラインと同期させる
      this.graphElementList.forEach((graphElement) => {
        // グラフ描画更新
        graphElement.updateMarker();
        graphElement.drawCurrentLine();
      });
      return;
    }

    // 更新された開始時間
    const newStartTimeMs = Number(values[this.timelineElement.getStartKnobNumber()]);
    // 更新された終了時間
    const newEndTimeMs = Number(values[this.timelineElement.getEndKnobNumber()]);

    // タイムライン描画更新
    this.timelineElement.setRangeSliderPosition(newStartTimeMs, newEndTimeMs);

    // グラフの表示範囲をタイムラインと同期させる
    this.updateGraphViewRange(newStartTimeMs, newEndTimeMs);

    if (this.timelineElement._handleRangeChange) {
      this.timelineElement._handleRangeChange(newStartTimeMs, newEndTimeMs);
    }
  }

  /**
   * グラフの開始・終了範囲の更新
   * @param {number} newStartTimeMs
   * @param {number} newEndTimeMs
   */
  updateGraphRange(newStartTimeMs, newEndTimeMs) {
    this.graphElementList.forEach((graphElement) => {
      // 開始時間・終了時間を保持
      graphElement.startTime = UTC.add(newStartTimeMs, 'ms');
      graphElement.endTime = UTC.add(newEndTimeMs, 'ms');
      // グラフ描画更新
      graphElement.updateComponent(UPDATE_TYPE.X_MIN_MAX, {
        "xMin": graphElement.startTime,
        "xMax": graphElement.endTime
      });
      graphElement.updateComponent(UPDATE_TYPE.X_TICKS, null);
      graphElement.updateMarker();
      graphElement.drawCurrentLine();
    });
  }

  /**
   * グラフの開始・終了の表示範囲の更新
   * @param {number} newStartTimeMs 新しい開始時間(ms)
   * @param {number} newEndTimeMs 新しい終了時間(ms)
   */
  updateGraphViewRange(newStartTimeMs, newEndTimeMs) {
    this.graphElementList.forEach((graphElement) => {
      // グラフ表示範囲の更新
      graphElement.applyZoomPanRange?.(newStartTimeMs, newEndTimeMs);
    });
  }

  play() {
    if (this.timerId === 0) {
      this.timerId = setInterval(this.playStep, TEN_MS, this.graphElementList, this.timelineElement);
    }
  }

  playStep(graphElementList, timelineElement) {
    // 再生条件（現在時刻 < 全体時間 )
    const playCondition = timelineElement.currentTimeMs < timelineElement._endTimeMs;

    if (!playCondition) {
      return;
    }
    // タイムライン描画更新
    // タイムラインの現在時刻を10ms足す
    timelineElement.currentTimeMs += TEN_MS;
    timelineElement.updateComponent();
    // グラフ描画更新(時間初期化 => グラフ更新 => 再生バー更新の流れ)
    graphElementList.forEach((graphElement) => {
      // グラフエレメントの現在時刻/現在事項msに10ms足す
      graphElement.currentTime = graphElement.currentTime.add(TEN_MS, 'ms');
      graphElement.drawCurrentLine();
    });
  }

  stop() {
    if (this.timerId !== 0) {
      clearTimeout(this.timerId);
    }
    // 再生開始時間を設定する
    if (this.timelineElement.currentTimeMs !== this.timelineElement._startTimeMs) {
      this.timelineElement.currentTimeMs = this.timelineElement._startTimeMs;
      this.graphElementList.forEach((graphElement) => {
        graphElement.currentTime = graphElement.currentTime.add(this.timelineElement.currentTimeMs, 'ms');
      });
    }
    // 時間を初期化
    this.graphElementList.forEach((graphElement) => {
      graphElement.updateComponent(UPDATE_TYPE.X_MIN_MAX, {
        "xMin": graphElement.startTime,
        "xMax": graphElement.endTime
      });

      // マーカラインとカレントラインを引き直す(CITS)
      graphElement.initTime();
      graphElement.currentTime = UTC.add(this.timelineElement.currentTimeMs, 'ms');
      graphElement.drawCurrentLine();
      graphElement.drawSetMarkerMain();
    });

    this.timelineElement.initTime();
    this.timelineElement.updateComponent();
    // timerIdを0に初期化
    this.timerId = 0;
  }

  pause() {
    // タイマ停止
    if (this.timerId !== 0) {
      clearTimeout(this.timerId);
    }
    // timerIdを0に初期化
    this.timerId = 0;
  }

  // 設定追加: 新しい描画線を追加する
  //  新しい描画線を追加で登録する
  addNewGraphLine(id, itemId, itemName, dataPoint, unit, min, max, minDisp, maxDisp, startTime, color, dataType, style, width = LINE_WIDTH.NORMAL, systemName = '') {
    if (!id) {
      throw new Error('id does not exist');
    }
    if (!itemName) {
      throw new Error('itemName does not exist');
    }
    if (!dataPoint) {
      throw new Error('dataPoint does not exist');
    }
    if (!unit) {
      throw new Error('unit does not exist');
    }
    if (!startTime) {
      throw new Error('startTime does not exist');
    }
    if (typeof id !== 'string') {
      throw new Error('id must be of type string');
    }
    if (typeof itemName !== 'string') {
      throw new Error('itemName must be of type string');
    }
    if (typeof dataPoint !== 'object') {
      throw new Error('dataPoint must be of type object');
    }
    if (typeof unit !== 'string') {
      throw new Error('unit must be of type string');
    }
    if (!isNaN(min) && typeof min !== 'number') {
      throw new Error('min must be of type number');
    }
    if (!isNaN(max) && typeof max !== 'number') {
      throw new Error('max must be of type number');
    }
    if (typeof startTime !== 'number') {
      throw new Error('startTime must be of type number');
    }

    if (this.graphElementList === undefined) {
      return;
    }

    // グラフに追加
    this.graphElementList.forEach((graphElement) => {
      if (graphElement.id === id) {

        // x軸の軸のメモリ間の間隔。デフォルトは1秒ごとに設定
        const addData = {};
        const grComp = graphElement.getComponent();
        const idNumArray = grComp.data.datasets.map((x) => Number(x.yAxisID.replace(/yaxis_/g, ""))).sort((a, b) => (a - b));
        let freeIndex = null;
        idNumArray.some((v, i) => {
          const rc = (v !== i);
          if (rc) {
            freeIndex = i;
          }
          return rc;
        });
        const lastInd = (freeIndex !== null) ? freeIndex : graphElement._yAxesInfo.length;
        Object.assign(addData, graphElement._yAxesInfo[0]);
        addData.unit = unit;
        addData.id = itemId;
        addData.itemName = itemName;
        addData.min = min;
        addData.max = max;
        addData.width = width;
        addData.lastIndex = lastInd; // Y軸ラベル参照用（datasetを本Indexで引くのはNG）
        addData.dataType = dataType;
        addData.color = color;
        if (systemName) {
          addData.system = systemName;
        }

        const data = dataPoint[0].map((timestamp, index) => {
          return { x: timestamp, y: dataPoint[1][index] };
        });
        // 全てnullであればdisplayValueは空配列を設定
        let displayValue = [];
        if (dataPoint[2].some(d => d !== null)) {
          displayValue = dataPoint[0].map((timestamp, index) => {
            return { x: timestamp, y: dataPoint[2][index] };
          }).filter(d => d.y);
        }

        const addDataset = {
          //Y軸ラベル
          label: itemId,
          yAxisID: 'yaxis_' + lastInd,
          //Y軸データ(各データの時間に対応)
          data: data,
          displayValue: displayValue,
          // ツールチップの色の背景色を設定(CITS)
          backgroundColor: color,
          // 下側の塗りつぶし色を設定しない(CITS)
          fill: false,
          //線の色
          borderColor: color,
          // 線の太さ
          borderWidth: graphElement.getBorderWidth(width),
          // 線のスタイル
          borderDash: style,
          pointRadius: 0
        };

        if (freeIndex !== null) {
          graphElement._yAxesInfo.splice(lastInd, undefined, addData);
          grComp.data.datasets.splice(lastInd, undefined, addDataset);
        } else {
          graphElement._yAxesInfo.push(addData);
          grComp.data.datasets.push(addDataset);
        }

        // Y軸ラベルを線ごとに消す
        for (const dataset of grComp.data.datasets) {
          grComp.options.scales[dataset.yAxisID] = {
            display: false
          };
        }

        //y軸ラベル変更用
        const elementId = `yLabelOuter${id}`;
        const elementPer = document.getElementById(elementId);
        const elementChild = elementPer.querySelectorAll('*');

        for (const element of elementChild) {
          const targetAttrType = element.id.includes("yLabelContainerMax") ? "max"
            : element.id.includes("yLabelContainerMin") ? "min"
              : element.id.includes("yLabelContainerItemName") ? "itemName" : null;

          if (targetAttrType === null) {
            continue;
          }
          // Y軸ラベルに表示する文字列を設定する
          GraphMediator.setYLabelAttr(element, targetAttrType, {
            id: id,
            index: lastInd,
            systemName: systemName,
            itemName: itemName,
            unit: unit,
            maxDisp: maxDisp,
            minDisp: minDisp,
            color: color,
          });
        }

        // Y軸のMax / Minを設定する
        grComp.data.datasets.map((x) => x.yAxisID).forEach((yAxisId, index) => {
          const yAxis = graphElement._graphData.options.scales[yAxisId];
          if (yAxis) {
            yAxis.max = graphElement._yAxesInfo[index].max;
            yAxis.min = graphElement._yAxesInfo[index].min;
          }
        });
        graphElement.drawCurrentLine();
        graphElement.drawCursorLegend();
      }
    });
  }

  //データポイント追加
  addDataPoint(id, dataPointList, clear = false) {
    if (!id) {
      throw new Error('id does not exist');
    }
    if (typeof id !== 'string') {
      throw new Error('id must be of type string');
    }
    if (!dataPointList) {
      throw new Error('dataPointList does not exist');
    }
    if (typeof dataPointList !== 'object') {
      throw new Error('dataPointList must be of type object');
    }

    // graph-elementの要素文ループ
    this.graphElementList.forEach((graphElement) => {
      // グラフのカレント時間をシークバーのカレント時間と同期させる
      graphElement.currentTime = UTC.add(this.timelineElement.currentTimeMs, 'ms');

      // graph-elementに設定されているidとaddDataPointで設定したいidが一致するか確認
      if (graphElement.id === id) {
        // graphのデータを取得
        graphElement._graphData.data.datasets.forEach((dataset, index) => {
          if (dataset.label in dataPointList) {
            // 一致したらlabel(X軸)とデータを更新
            const data = this.createArray(dataPointList[dataset.label], clear);
            // データチェック
            if (data[0].length === 0) {
              return;
            }
            if (data[0].length !== data[1].length) {
              return;
            }
            if (data[0].length > ONE_DAY_MS) {
              return;
            }
            if (clear) {
              dataset.data = [];
              dataset.displayValue = [];
            }

            data[0].forEach((timestamp, timestampIndex) => {
              dataset.data.push({ x: timestamp, y: data[1][timestampIndex] });
              if (data[3] && data[3][timestampIndex]) {
                dataset.displayValue.push({ x: timestamp, y: data[3][timestampIndex] });
              }
            });

            // Y軸のMax / Minを設定する
            const yAxis = graphElement._graphData.options.scales[dataset.yAxisID];
            if (yAxis) {
              yAxis.ticks.max = graphElement._yAxesInfo[index].max;
              yAxis.max = yAxis.ticks.max;
              yAxis.ticks.min = graphElement._yAxesInfo[index].min;
              yAxis.min = yAxis.ticks.min;
            }

            // タイムラインの情報更新
            this.timelineElement.updateComponent(data[2]);

            // グラフの再描画
            if (this.drawMethod === DRAW_METHOD_ALL) {
              // 方式1: 計測開始時点を起点としてデータを積み上げして表示する方式
              graphElement.startTime = UTC.add(this.timelineElement._startTimeMs, 'ms');
              graphElement.endTime = UTC.add(this.timelineElement._endTimeMs, 'ms');
            } else if (this.drawMethod === DRAW_METHOD_RECENT) {
              // 方式2: 最新データより一定区間のデータを表示する方式(ストリーミング再生)
              const timeSec = Math.ceil(data[2] / ONE_SECOND_MS); // 最新のタイムスタンプを秒単位で切り上げ
              graphElement.startTime = UTC.add(timeSec - (this.stepSize * (this.stepSize <= 5 ? 25 : 29.5)), 's'); // 左端: 最新のタイムスタンプよりステップサイズ(s)×25個程度前を設定、秒数によって個数が異なるので調整(20s,60sの場合)
              graphElement.endTime = UTC.add(timeSec, 's'); // 右端: 最新のタイムスタンプを秒単位で切り上げた時間
            }

            graphElement.updateComponent(
              UPDATE_TYPE.X_MIN_MAX,
              {
                "xMin": graphElement.startTime,
                "xMax": graphElement.endTime
              },
              true
            );
          }
        });
      }
    });
  }

  createArray(array, sort) {
    // 過去のデータを後から追加する場合はsort=trueとなるが、現状はそのようなユースケースがないため利用しない
    sort;
    // objectから2次元配列を作る
    const keys = Object.keys(array);
    const keysArray = keys.map((key) => Number(key));
    const values = Object.values(array).map(i => i[0]);
    const displayValues = Object.values(array).map(i => i[1] || null);
    return [keysArray, values, parseInt(keys[keys.length - 1], 10), displayValues]; // 10進数指定を追加(CITS)
  }

  /**
   * グラフの最大値と最小値の設定をクリアする
   * @param {string} id 最大・最小をクリアするY軸情報のID
   */
  clearMinMaxSettings(id) {
    for (const graphElement of this.graphElementList) {
      const targetIndex = graphElement._yAxesInfo.findIndex(yAxis => yAxis.id === id);
      if (targetIndex !== -1) {
        graphElement._yAxesInfo[targetIndex] = {
          ...graphElement._yAxesInfo[targetIndex],
          min: undefined,
          max: undefined
        };
        const emptyValue = graphElement.formatYLabelText(null);
        const lastInd = graphElement._yAxesInfo[targetIndex].lastIndex;
        this.updateYLabel(graphElement.id, lastInd, emptyValue, emptyValue);
        // Y軸のMax / Minを設定する
        const yAxisId = 'yaxis_' + lastInd;
        const yAxis = graphElement._graphData.options.scales[yAxisId];
        if (yAxis) {
          yAxis.max = undefined;
          yAxis.min = undefined;
        }
        graphElement.drawCurrentLine();
      }
    }
  }

  /**
   * Y軸のラベルを更新する
   * @param {string} id 更新するY軸情報のID
   * @param {number} lastIndex 更新するY軸情報が何番目かを指定
   * @param {string} max Y軸ラベルに設定する最大値の文字列
   * @param {string} min Y軸ラベルに設定する最小値の文字列
   */
  updateYLabel(id, lastIndex, max, min) {
    const elementChild = document.getElementById(`yLabelOuter${id}`).querySelectorAll('*');

    for (const element of elementChild) {
      const idPrefix = element.id.includes("yLabelContainerMax") ? "max" :
        element.id.includes("yLabelContainerMin") ? "min" : null;

      if (idPrefix === null) {
        continue;
      }

      const labelElement = element.getElementsByTagName("p");
      // ラベルのindexを取得
      const targetId = `${idPrefix}${id}${lastIndex}`;
      const targetIndex = Array.from(labelElement).findIndex(item => item.id === targetId);
      if (targetIndex === -1) {
        continue;
      }

      labelElement[targetIndex].textContent = (idPrefix === "max") ? max : min;
    }
  }

  /**
   * Y軸のラベルの色を更新する
   * @param {string} id 更新するY軸情報のID
   * @param {number} lastIndex 更新するY軸情報が何番目かを指定
   * @param {string} color 色
   */
  updateYLabelColor(id, color, lastIndex = 0) {
    const elementChild = document.getElementById(`yLabelOuter${id}`).querySelectorAll('*');

    for (const element of elementChild) {
      const idPrefix = element.id.includes("yLabelContainerMax") ? "max" :
        element.id.includes("yLabelContainerMin") ? "min" :
          element.id.includes("yLabelContainerItemName") ? "itemName" : null;

      if (idPrefix === null) {
        continue;
      }

      const labelElement = element.getElementsByTagName("p");
      // ラベルのindexを取得
      const targetId = `${idPrefix}${id}${lastIndex}`;
      const targetElements = Array.from(labelElement);
      const targetIndex = targetElements.findIndex(item => item.id === targetId);
      if (targetIndex === -1) {
        continue;
      }

      labelElement[targetIndex].style.color = color;
    }
  }

  /**
   * グラフに表示する翻訳済み文字列の設定
   * @param {string} max 凡例の"最大"の翻訳済み文字列
   * @param {string} min 凡例の"最小"の翻訳済み文字列
   * @param {string} avg 凡例の"平均"の翻訳済み文字列
   * @param {string} value 凡例の"値"の翻訳済み文字列
   */
  setTranslatedValue(max, min, avg, value) {
    this.graphElementList.forEach((graphElement) => {
      graphElement.setTranslatedValue({
        [LEGEND_DEFS.MAX]: max,
        [LEGEND_DEFS.MIN]: min,
        [LEGEND_DEFS.AVG]: avg,
        [LEGEND_DEFS.VALUE_A]: `${value} ${c.CURSOR.A}`,
        [LEGEND_DEFS.VALUE_B]: `${value} ${c.CURSOR.B}`,
      });
    });
  }

  // グラフの色・太さ・スタイルを変更
  // eslint-disable-next-line complexity
  updateGraphLineSetting(id, color, width, style, label = null) {
    if (!id) {
      throw new Error('id does not exist');
    }
    if (id && typeof id !== 'string') {
      throw new Error('id must be of type string');
    }
    // 色
    if (color && typeof color !== 'string') {
      throw new Error('color must be of type string');
    }
    if (color && !COLORCODE.test(color)) {
      throw new Error('not a supported color');
    }
    // 太さ
    if (width !== null && width !== undefined && typeof width !== 'number') {
      throw new Error('width must be of type number');
    }
    if (width !== null && width !== undefined && !Object.values(LINE_WIDTH).includes(width)) {
      throw new Error('width must be 1, 2, or 3');
    }
    // スタイル
    if (style !== null && style !== undefined && typeof style !== 'number') {
      throw new Error('style must be of type number');
    }
    if (style !== null && style !== undefined && !Object.values(STYLE).includes(style)) {
      throw new Error('style must be 1, 2, or 3');
    }
    if (label !== null) {
      if (label !== undefined && typeof label !== 'string') {
        throw new Error('label must be of type string');
      }
    }

    // グラフ描画領域分ループ
    this.graphElementList.forEach((gel) => {
      this.updateGraphLineSettingForGraphElementList(gel, id, color, width, style, label);
    });
  }

  updateGraphLineSettingForGraphElementList(gel, id, color, width, style, label) {
    // グラフ表示形式を変更したいID確認
    if (gel.id === id) {
      const ds = label !== null ? gel._graphComponent.data.datasets.find(dataset => dataset.label === label) : gel._graphComponent.data.datasets[0];  // 変更する描画データセットを取得
      const yAxesInfo = label !== null ? gel._yAxesInfo.find(yAxes => yAxes.id === label) : gel._yAxesInfo[0]; // グラフY軸情報を取得

      if (ds !== null) {
        // 描画色変更 (引数がnullの場合は色変更しない)
        if (color !== null) {
          ds.borderColor = color;
          ds.backgroundColor = color;
          yAxesInfo.color = color;
        }

        // 描画線の幅変更 (引数が1/2/3以外の場合は変更しない)
        switch (width) {
          case 1:
            ds.borderWidth = THIN;
            break;
          case 2:
            ds.borderWidth = NORMAL;
            break;
          case 3:
            ds.borderWidth = THICK;
            break;
          default:
            break;
        }
        yAxesInfo.width = width;

        // 描画線のスタイル変更 (引数が1/2/3以外の場合は変更しない)
        switch (style) {
          case 1:
            ds.borderDash = SOLID; // 実線
            break;
          case 2:
            ds.borderDash = DASHED; // 破線
            break;
          case 3:
            ds.borderDash = DOTTED; // 点線
            break;
          default:
            break;
        }
        yAxesInfo.style = style;
      }
    }

    // 描画更新
    gel.drawCurrentLine();
  }

  /**
   * タイムラインの現在時刻をdeltaMs分だけ進める/戻す
   * @param {number} deltaMs 進める/戻す時間(ms)。正の値の場合は進める、負の値の場合は戻す
   */
  stepBy(deltaMs) {
    let nextCurrentMs = this.timelineElement.currentTimeMs + deltaMs;
    // 現在時刻を進める/戻す時間が、タイムラインの範囲外にならないようにする。表示範囲の外には行ける。
    if (nextCurrentMs < this.timelineElement.rangeMinMs) {
      nextCurrentMs = this.timelineElement.rangeMinMs;
    } else if (nextCurrentMs > this.timelineElement.rangeMaxMs) {
      nextCurrentMs = this.timelineElement.rangeMaxMs;
    }
    this.seekToTime(nextCurrentMs);
  }

  // <<
  stepBackMax() {
    this.stepBy(-this.timelineElement.stepSizeLarge);
  }

  // <
  stepBackMin() {
    this.stepBy(-this.timelineElement.stepSizeSmall);
  }

  // >
  stepFwdMin() {
    this.stepBy(this.timelineElement.stepSizeSmall);
  }

  // >>
  stepFwdMax() {
    this.stepBy(this.timelineElement.stepSizeLarge);
  }

  // グラフのマーカ登録処理
  graphSetMarker(time = null) {
    this.graphElementList.forEach((graphElement) => {
      graphElement.graphSetMarker(time);
    });
  }

  /**
   * カーソル追加
   * @param {string} cursorType カーソル種別
   * @param {object} usingColors キーに項目ID、値に使用中の色を持つオブジェクト
   * @param {number} time カーソルを追加する時間
   */
  setCursor(cursorType, time = null) {
    this.timelineElement.deleteCursor(cursorType);
    this.timelineElement.setCursor(cursorType, time);
    this.graphElementList.forEach((graphElement) => {
      graphElement.graphSetCursor(cursorType, time);
    });
  }

  /**
   * 全カーソル情報削除
   */
  deleteCursor() {
    this.timelineElement.deleteCursor(c.CURSOR.A);
    this.timelineElement.deleteCursor(c.CURSOR.B);
    this.graphElementList.forEach((graphElement) => {
      graphElement.clearCursor();
    });
  }

  /**
   * 全グラフのズーム・パン状態をリセット
   * @param {boolean} propagateToLinked 連携しているMediatorにも同期するか否か(デフォルト:false)。
   */
  resetZoom(propagateToLinked = false) {
    this.graphElementList.forEach((graphElement) => {
      graphElement.resetZoom && graphElement.resetZoom();
    });

    this.zoomPanStateChangedCallback(false);

    // グラフリセット後、それに合わせてタイムラインの範囲もリセットする。グラフの時間はすべて同じのため、最初のグラフのX軸範囲をタイムラインの範囲に設定。
    const chart = this.graphElementList[0]?.getComponent?.();
    const xScale = chart?.scales?.x;
    if (xScale) {
      this.syncTimelineRange(xScale.min, xScale.max);
    }

    if (!propagateToLinked || !Array.isArray(this.linkedMediators) || this.linkedMediators.length <= 0) {
      return;
    }

    // ダブルクリックでは分割グラフ→結合グラフ、
    // もしくは結合グラフ→分割グラフでリセットを呼び出す必要があるためMediator経由でリセットする。
    this.linkedMediators.forEach((mediator) => {
      mediator.resetZoom(false);
    });
  }

  /**
   * ズーム/パンで変更されたX/Y軸範囲を他のグラフへ同期する
   * @param {string} sourceGraphId 操作元のグラフID。プルダウン操作の場合はnullを受け取り、全てのグラフを同期対象とする。
   * @param {number} xMin X軸の最小値(ms)
   * @param {number} xMax X軸の最大値(ms)
   * @param {object|null} yRanges Y軸の範囲。
   * @param {boolean} propagateToLinked 連携しているMediatorにも同期するか否か(デフォルト:true)
   */
  syncZoomPanRange(sourceGraphId, xMin, xMax, yRanges = null, propagateToLinked = true) {
    if (!Number.isFinite(xMin) || !Number.isFinite(xMax)) {
      return;
    }

    this.graphElementList.forEach((graphElement) => {
      if (graphElement._id === sourceGraphId) {
        // 操作したグラフ自身は同期処理の対象外
        return;
      }
      // ただしプルダウン操作時はsyncZoomPanRangeで自身の倍率も更新するため、sourceGraphIdがnullのときは全てのグラフを同期対象とする
      graphElement.applyZoomPanRange(xMin, xMax, yRanges);
    });

    if (sourceGraphId === null) {
      // プルダウン操作(sourceGraphIdがnull)の場合は、このタイミングでグラフ設定ボタンを非活性にするためコールバックを呼ぶ。
      // その他の操作時は、ズーム・パン完了時のコールバックでグラフ設定ボタンを非活性にしているため、呼び出し不要。
      this.zoomPanStateChangedCallback(false);
    }

    // タイムラインの範囲もズーム・パンしたグラフのX軸範囲に合わせる
    this.syncTimelineRange(xMin, xMax);

    if (!propagateToLinked || !Array.isArray(this.linkedMediators) || this.linkedMediators.length <= 0) {
      return;
    }

    this.linkedMediators.forEach((mediator) => {
      mediator.syncZoomPanRange(sourceGraphId, xMin, xMax, yRanges, false);
    });
  }

  /**
   * タイムラインの範囲を同期する
   * @param {number} xMin X軸の最小値(ms)
   * @param {number} xMax X軸の最大値(ms)
   */
  syncTimelineRange(xMin, xMax) {
    if (!this.timelineElement || !Number.isFinite(xMin) || !Number.isFinite(xMax)) {
      return;
    }
    this.timelineElement.setRangeSliderPosition(xMin, xMax);
  }

  /**
   * 全グラフのカーソル更新
   * @param {string} cursorItem カーソル対象に指定する項目ID(e.g. ABG_25ePF\t101A_1_all)
   */
  updateAllGraphCursor(cursorItem) {
    this.graphElementList.forEach((graphElement) => {
      graphElement.updateCursor(cursorItem);
    });
  }

  /**
   * グラフのリサイズを実施。
   * 計測中はグラフのリサイズを止めているため、呼び出し時のみリサイズを実施
   */
  resize() {
    this.graphElementList && this.graphElementList.forEach((graphElement) => {
      graphElement.resize();
    });
  }

  // バリテーション
  validation() {
    const graphElementList = document.querySelectorAll('graph-element');
    const timelineElement = document.querySelectorAll('time-line')[0];
    graphElementList.forEach((graphElement) => {
      graphElement.validation();
    });
    timelineElement.validation();
  }

  /**
   * 経過時刻文字列の作成
   * @param {number} timeMSec ミリ秒
   * @param {boolean} isMsec 100ミリ秒単位で表示するか否か
   * @returns h:mm:ss.SSS or h:mm:ss
   */
  static getPassedTimeStr(timeMSec, isMsec) {
    const HOUR_IN_MS = 60 * 60 * 1000;
    const MINUTE_IN_MS = 60 * 1000;
    const SECOND_IN_MS = 1000;

    const hours = Math.floor(timeMSec / HOUR_IN_MS);
    const minutes = Math.floor((timeMSec % HOUR_IN_MS) / MINUTE_IN_MS);
    const seconds = Math.floor((timeMSec % MINUTE_IN_MS) / SECOND_IN_MS);
    const milliseconds = Math.floor(timeMSec) % SECOND_IN_MS;

    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');
    const formattedMSec = String(milliseconds).padStart(3, '0');

    if (hours < 0 || minutes < 0 || seconds < 0 || milliseconds < 0) {
      return isMsec ? DEFAULT_PASSED_TIME_MSEC : DEFAULT_PASSED_TIME_SEC;
    }

    const timeStr = `${hours > 0 ? `${hours}:` : ''}${formattedMinutes}:${formattedSeconds}`;
    return isMsec ? `${timeStr}.${formattedMSec}` : timeStr;
  }

  /**
   * Y軸ラベルに文字情報やid等を設定する
   * @param {object} parentAttr 要素を追加する先の親要素
   * @param {string} attrType 追加する要素の種類（itemName or min or max）
   * @param {object} params 要素追加時に設定するパラメータ
   */
  static setYLabelAttr(parentAttr, attrType, params) {
    const attr = document.createElement("p");
    attr.setAttribute("id", `${attrType}${params.id}${params.index}`);
    attr.setAttribute("data-testid", attrType);
    attr.style.color = params.color ? params.color : COLOR.DEFAULT;

    if (attrType === "itemName") {
      // 項目名と単位は異なる要素として追加する
      const itemNamePTag = document.createElement("span");
      itemNamePTag.textContent = params.systemName ? `[${params.systemName}] ${params.itemName}` : params.itemName;
      attr.appendChild(itemNamePTag);

      const unitPTag = document.createElement("span");
      unitPTag.textContent = `[${params.unit}]`;
      attr.appendChild(unitPTag);
    } else {
      const contentValue = params[`${attrType}Disp`] || params[attrType];
      attr.textContent = contentValue || contentValue === 0 ? contentValue : "-";
    }
    parentAttr.appendChild(attr);
  }

  /**
   * 基準時刻からの経過時刻文字列の作成
   * @param {number} baseTimeMSec 基準時刻（ミリ秒）
   * @param {number} currentTimeMSec 現在時刻（ミリ秒）
   * @param {boolean} isMsec 100ミリ秒単位で表示するか否か
   * @returns h:mm:ss.SSS or h:mm:ss
   */
  static getPassedTimeStrFromBaseTime(baseTimeMSec, currentTimeMSec, isMsec) {
    if (!baseTimeMSec) {
      return isMsec ? DEFAULT_PASSED_TIME_MSEC : DEFAULT_PASSED_TIME_SEC;
    }

    const elapsedMSec = currentTimeMSec - baseTimeMSec;
    return GraphMediator.getPassedTimeStr(elapsedMSec, isMsec);
  }

}
